import React, { useState, useEffect, useRef } from "react";
import { PatientLanguageSelect } from "./PatientLanguageSelect";
import api from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  CalendarClock, Syringe, FlaskConical, BookOpenText, Sparkles, Loader2, Plus,
  ArrowRight, Languages,
} from "lucide-react";

const URGENCY_STYLE = {
  urgent: "bg-red-50 text-red-700 border-red-200",
  "semi-urgent": "bg-amber-50 text-amber-700 border-amber-200",
  routine: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function NextStepsPanel({
  patientId, snapshot, onSetFollowUp, onAddInvestigation, onAppendAdvice, onAcceptForPrint, preferredLanguage,
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [lang, setLang] = useState("en");
  const [accepted, setAccepted] = useState(false);
  const requestId = useRef(0);
  useEffect(() => {
    requestId.current += 1;
    setLang(preferredLanguage || "en"); setData(null); setAccepted(false); setLoading(false);
  }, [patientId, preferredLanguage]);

  const compute = async () => {
    const req = ++requestId.current;
    setLoading(true); setErr(""); setData(null); setAccepted(false);
    try {
      const r = await api.post("/ai/next-steps", { patient_id: patientId, snapshot, language: lang }, { timeout: 120000 });
      if (req !== requestId.current) return;
      setData(r.data.next_steps || {});
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Failed to compute next steps");
    } finally { if (req === requestId.current) setLoading(false); }
  };

  const acceptAllForPrint = () => {
    if (!data) return;
    const eduFieldNow = "text_local";
    const nf = data.next_followup || {};
    // MT-11B.7: accepting the block for PRINT never changes the visit's
    // follow-up — only the explicit "Apply" button does. The saved
    // follow_up_date stays canonical; the suggestion prints as a note.
    if (nf.in_days && onSetFollowUp) {
      toast.info(`Suggested follow-up (${nf.in_days} days) not applied — use Apply to adopt it`, { duration: 5000 });
    }
    (data.next_labs || []).forEach((l) => l.lab && onAddInvestigation && onAddInvestigation(l.lab));
    (data.patient_education || []).forEach((e) => {
      const txt = e[eduFieldNow];
      if (txt && onAppendAdvice) onAppendAdvice(`• ${e.topic_local || e.topic}: ${txt}`);
    });
    if (onAcceptForPrint) {
      onAcceptForPrint({
        summary: data.summary || "",
        next_followup: data.next_followup || {},
        next_vaccines: data.next_vaccines || [],
        next_labs: data.next_labs || [],
        patient_education: (data.patient_education || []).map((e) => ({
          topic: e.topic_local || e.topic,
          text: e[eduFieldNow],
          text_en: e.text_en || "",
          text_hi: e.text_hi || "",
          text_mr: e.text_mr || "",
        })),
        handout_lang: lang,
        accepted_at: new Date().toISOString(),
      });
    }
    setAccepted(true);
    toast.success("Next steps accepted — will print on the Rx");
  };

  const ns = data || {};
  const followup = ns.next_followup || {};
  const vaccines = ns.next_vaccines || [];
  const labs = ns.next_labs || [];
  const education = ns.patient_education || [];
  const eduField = "text_local";

  return (
    <section
      className="border border-pink-200 bg-gradient-to-br from-pink-50/60 to-white rounded-md p-4"
      data-testid="next-steps-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles size={14} className="text-pink-600" /> Next Steps (AI)
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Follow-up date, next vaccines, next lab tests and patient education —
            tuned to age, complaints, diagnosis, current Rx and latest labs.
          </p>
        </div>
        {!data && !loading && (
          <Button
            size="sm"
            onClick={compute}
            className="bg-pink-600 hover:bg-pink-700 text-white gap-1.5 h-8 text-xs"
            data-testid="next-steps-compute"
          >
            <Sparkles size={12} /> Compute
          </Button>
        )}
        {data && !loading && (
          <button
            onClick={compute}
            className="text-[11px] text-pink-700 hover:underline"
            data-testid="next-steps-refresh"
          >
            Refresh
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-slate-600 py-6 justify-center">
          <Loader2 size={16} className="animate-spin text-pink-600" />
          Reasoning over the patient snapshot…
        </div>
      )}
      {err && !loading && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2.5" data-testid="next-steps-error">{err}</div>
      )}

      {!loading && !data && !err && (
        <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-md p-4 text-center">
          Tap <strong>Compute</strong> to get context-aware next steps.
        </div>
      )}

      {data && !loading && (
        <div className="space-y-3">
          {ns.summary && (
            <div className="text-xs italic text-slate-600 border border-slate-200 rounded-md p-2.5 bg-white" data-testid="next-steps-summary">
              {ns.summary}
            </div>
          )}

          {onAcceptForPrint && (
            <div className="flex items-center justify-between gap-3 border border-pink-300 bg-pink-50/60 rounded-md px-3 py-2">
              <div className="text-[11px] text-pink-900">
                <strong>Accept & print all:</strong> apply follow-up, add labs, copy education in <em className="uppercase">{lang}</em>, and include this handout on the printed Rx.
              </div>
              <Button
                size="sm"
                onClick={acceptAllForPrint}
                className={`h-7 text-[11px] gap-1 ${accepted ? "bg-emerald-600 hover:bg-emerald-700" : "bg-pink-600 hover:bg-pink-700"} text-white`}
                data-testid="next-steps-accept-all"
              >
                {accepted ? "Accepted ✓ (re-apply)" : "Accept for Rx"}
              </Button>
            </div>
          )}

          {/* Follow-up */}
          {(followup.date_iso || followup.in_days) && (
            <div className={`border rounded-md p-3 ${URGENCY_STYLE[followup.urgency] || URGENCY_STYLE.routine}`} data-testid="next-followup">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1"><CalendarClock size={11} /> Next follow-up</div>
                  <div className="text-sm font-semibold mt-0.5">
                    {followup.date_iso}
                    {followup.in_days && <span className="ml-2 font-normal text-[11px] opacity-75">(in {followup.in_days} days)</span>}
                  </div>
                  {followup.reason && <div className="text-[11px] mt-1 opacity-90">{followup.reason}</div>}
                </div>
                {onSetFollowUp && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      onSetFollowUp({
                        follow_up: followup.in_days ? `${followup.in_days} days` : "",
                        follow_up_date: followup.date_iso || "",
                      });
                      toast.success("Follow-up applied to visit");
                    }}
                    className="h-7 text-[11px] gap-1 bg-white border-current"
                    data-testid="next-followup-apply"
                  >
                    <ArrowRight size={11} /> Apply
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Next vaccines */}
          {vaccines.length > 0 && (
            <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid="next-vaccines">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1 mb-2">
                <Syringe size={11} /> Next vaccines ({vaccines.length})
              </div>
              <ul className="space-y-1.5">
                {vaccines.map((v, i) => (
                  <li key={`nv-${v.name || i}`} className="text-xs flex items-start justify-between gap-2 border-t border-slate-100 pt-1.5 first:border-t-0 first:pt-0" data-testid={`next-vaccine-${i}`}>
                    <div>
                      <span className="font-medium">{v.name}</span>
                      {v.due && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">{v.due}</span>}
                      {v.why && <div className="text-[11px] text-slate-500 mt-0.5">{v.why}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Next labs */}
          {labs.length > 0 && (
            <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid="next-labs">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1 mb-2">
                <FlaskConical size={11} /> Next lab tests ({labs.length})
              </div>
              <ul className="space-y-1.5">
                {labs.map((l, i) => (
                  <li key={`nl-${l.lab || i}`} className="text-xs flex items-start justify-between gap-2 border-t border-slate-100 pt-1.5 first:border-t-0 first:pt-0" data-testid={`next-lab-${i}`}>
                    <div className="flex-1">
                      <span className="font-medium">{l.lab}</span>
                      {l.when && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{l.when}</span>}
                      {l.reason && <div className="text-[11px] text-slate-500 mt-0.5">{l.reason}</div>}
                    </div>
                    {onAddInvestigation && (
                      <button
                        onClick={() => { onAddInvestigation(l.lab); toast.success(`${l.lab} added to next investigations`); }}
                        className="text-[10px] text-pink-700 hover:underline flex items-center gap-0.5 shrink-0"
                        data-testid={`next-lab-add-${i}`}
                      >
                        <Plus size={10} /> add
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Patient education */}
          {education.length > 0 && (
            <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid="patient-education">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1">
                  <BookOpenText size={11} /> Patient education ({education.length})
                </div>
                <PatientLanguageSelect value={lang} disabled testId="next-steps-language" label="Patient language" />
              </div>
              <ul className="space-y-2">
                {education.map((e, i) => (
                  <li key={`ed-${e.topic || i}`} className="text-xs border-t border-slate-100 pt-2 first:border-t-0 first:pt-0" data-testid={`edu-item-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">{e.topic_local || e.topic}</div>
                        <div lang={lang} dir="auto" data-testid={`next-steps-education-text-${i}`} className="text-[11px] text-slate-700 mt-0.5 leading-relaxed">{e[eduField]}</div>
                      </div>
                      {onAppendAdvice && (
                        <button
                          onClick={() => { onAppendAdvice(`• ${e.topic_local || e.topic}: ${e[eduField]}`); toast.success("Copied to advice"); }}
                          className="text-[10px] text-pink-700 hover:underline flex items-center gap-0.5 shrink-0"
                          data-testid={`edu-add-${i}`}
                        >
                          <Plus size={10} /> to advice
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
